import { useEffect, useMemo, useState } from 'react'
import { FormProvider, useFieldArray, useForm, type FieldErrors, type Resolver } from 'react-hook-form'
import { CalculatorIcon, PlusCircleIcon, TrashSimpleIcon } from '@phosphor-icons/react'
import { Box, useMediaQuery } from '@mui/material'

import { BaseModal2 } from '@components/modals/BaseModal2'
import { emotionTheme } from '@styles/theme'
import { initialFormValues } from './form/initialFormValues'
import { Input } from '@components/shared/Input'
import { ReturnModal } from '@components/modals/ReturnModal'
import { adjustPreviewModalSchema } from './form/validations'
import { RowBox } from '@components/shared/RowBox'
import { setInitialValues } from './form/setInitialValues'
import { WarningIcon } from '@assets/svgs/WarningIcon'
import { Select } from '@components/shared/Select'
import * as apiPreview from '@services/api/preview'

import type { AdjustPreviewModalProps } from './types'
import type { AdjustPreviewFormProps } from './form/types'

import * as S from './styles'

export const AdjustPreviewModal = ({
  open,
  onClose,
  userCanEdit = false,
  branchCode,
  periodSelectedData,
  adjustPreviewListLoading,
  consolidatedPreviewFiltersData,
  saveNewAdjustPreview,
  openDeleteAdjustPreviewModal,
  setOpenDeleteAdjustPreviewModal,
  deleteAdjustPreviewLoading,
  deleteAdjustPreviewById,
  saveAdjustPreviewLoading,
  openAdjustPreviewModal,
  setOpenAdjustPreviewModal,
  userData,
  ...rest
}: AdjustPreviewModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)

  const methods = useForm<AdjustPreviewFormProps>({
    defaultValues: initialFormValues,
    resolver: adjustPreviewModalSchema as unknown as Resolver<AdjustPreviewFormProps>
  })

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: 'accounts'
  })

  const [openCancelDetailedSummaryModal, setOpenCancelDetailedSummaryModal] = useState(false)
  const [deleteAdjustPreviewId, setDeleteAdjustPreviewId] = useState<string | null>(null)
  const [adjustPreviewFormData, setAdjustPreviewFormData] = useState<AdjustPreviewFormProps | null>(null)
  const [contasSemanaAtual, setContasSemanaAtual] = useState<{ code: string; name: string }[]>([])
  const [contasSemanaAtualLoading, setContasSemanaAtualLoading] = useState(false)
  const [currentWeekNumber, setCurrentWeekNumber] = useState<number>(0)

  // Opções para linhas novas: apenas contas do endpoint contas-semana-atual
  const newAccountOptions = useMemo(() => {
    return contasSemanaAtual.map((account) => ({ label: account.name, value: account.name }))
  }, [contasSemanaAtual])

  // Opções para linhas existentes (carregadas da API): inclui contas que podem não
  // estar em contas-semana-atual, usando o nome como fallback de value
  const handleAddAccount = () => append({ account: '', adjustmentValue: '', observations: '' })

  const onError = (errors: FieldErrors<AdjustPreviewFormProps>) => console.log({ errors })

  const onSubmit = (formData: AdjustPreviewFormProps) => {
    setAdjustPreviewFormData(formData)
    setOpenAdjustPreviewModal(true)
  }

  useEffect(() => {
    if (open) {
      methods.reset(setInitialValues(periodSelectedData, currentWeekNumber))
    }
  }, [periodSelectedData, open, currentWeekNumber])

  useEffect(() => {
    if (open) {
      setContasSemanaAtualLoading(true)
      apiPreview
        .getContasSemanaAtual(periodSelectedData?.competenceDate ?? '', branchCode)
        .then((data) => {
          setContasSemanaAtual(data?.accounts ?? [])
          setCurrentWeekNumber(data?.currentWeekNumber ?? 0)
        })
        .catch(() => {
          setContasSemanaAtual([])
          setCurrentWeekNumber(0)
        })
        .finally(() => setContasSemanaAtualLoading(false))
    } else {
      setContasSemanaAtual([])
      setCurrentWeekNumber(0)
    }
  }, [open, periodSelectedData, branchCode])

  useEffect(() => {
    !open && methods.reset(initialFormValues)
  }, [open])

  return (
    <>
      <BaseModal2
        open={open}
        title="Ajustar Prévia"
        headerIcon={<CalculatorIcon />}
        onClose={() => setOpenCancelDetailedSummaryModal(true)}
        width={isMobile ? 'calc(100% - 20px)' : 870}
        footer={{
          cancelButtonText: 'Voltar',
          cancelButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          loading: false,
          ...(userCanEdit && {
            confirmButtonText: 'Salvar',
            confirmButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
            onConfirm: () => methods.handleSubmit(onSubmit, onError)()
          })
        }}
        loading={adjustPreviewListLoading || contasSemanaAtualLoading}
        {...rest}
      >
        <S.Title>Informe o valor do ajuste e preencha o campo "Observações" antes de confirmar.</S.Title>

        <FormProvider {...methods}>
          <S.Form>
            <RowBox columns={4}>
              <Box sx={{ gridColumn: 'span 2' }}>
                <Input name="period" label="Período Competência" required disabled />
              </Box>
              <Input name="dateCompetence" label="Data Competência" required disabled />
              <Input name="week" label="Semana" disabled />
            </RowBox>

            {fields.map((field, index) => (
              <S.InputsBox key={field.id}>
                <RowBox columns={4}>
                  <Select
                    required
                    label="Conta"
                    name={`accounts.${index}.account`}
                    options={newAccountOptions}
                    disabled={!!(!!fields[index]?.accountId && fields[index].creationUserCode !== userData?.id)}
                  />
                  <Input
                    name={`accounts[${index}].adjustmentValue`}
                    label="Valor Ajuste (R$)"
                    placeholder="R$ 0,00"
                    maskType="currency"
                    disabled={!userCanEdit || !!(!!fields[index]?.accountId && fields[index].creationUserCode !== userData?.id)}
                  />

                  <Box style={{ gridColumn: 'span 2', display: 'flex', gap: 16, alignItems: 'center' }}>
                    <Input
                      required
                      name={`accounts[${index}].observations`}
                      label="Observações"
                      disabled={!userCanEdit || !!(!!fields[index]?.accountId && fields[index].creationUserCode !== userData?.id)}
                    />
                    {userCanEdit && (
                      <Box style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: -30 }}>
                        <S.AddAccountButton onClick={handleAddAccount}>
                          <PlusCircleIcon />
                        </S.AddAccountButton>

                        {(!index && !fields[index].accountId) || !!(fields[index]?.accountId && fields[index].creationUserCode !== userData?.id) ? null : (
                          <S.AddAccountButton onClick={() => {
                            if (fields[index].accountId) {
                              setDeleteAdjustPreviewId(fields[index].accountId!)
                              setOpenDeleteAdjustPreviewModal(true)
                            } else {
                              remove(index)
                            }
                          }}>
                            <TrashSimpleIcon />
                          </S.AddAccountButton>
                        )}
                      </Box>
                    )}
                  </Box>
                </RowBox>

              </S.InputsBox>
            ))}
          </S.Form>
        </FormProvider>

        {/* <Table
          data={parseAdjustPreviewGrid({
            data: [
              {
                account: '123',
                actualPreview: '100',
                budgetPercentage: '10',
                adjustmentValue: '10'
              }
            ]
          })}
        /> */}
      </BaseModal2>

      <ReturnModal
        open={openCancelDetailedSummaryModal}
        headerIcon={<WarningIcon />}
        title="Deseja realmente cancelar a ação?"
        message="Todo o seu progresso será perdido!"
        headerTitle="Você tem certeza?"
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenCancelDetailedSummaryModal}
        onConfirm={() => {
          onClose(false)
          setOpenCancelDetailedSummaryModal(false)
        }}
      />

      <ReturnModal
        open={openAdjustPreviewModal}
        headerIcon={<CalculatorIcon size={24} weight="bold" />}
        headerTitle="Ajustar Prévia"
        message="Tem certeza de que deseja ajustar a Prévia?"
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenAdjustPreviewModal}
        isLoading={saveAdjustPreviewLoading}
        onConfirm={() => saveNewAdjustPreview(adjustPreviewFormData!)}
      />

      <ReturnModal
        open={openDeleteAdjustPreviewModal}
        headerIcon={<TrashSimpleIcon size={24} weight="bold" />}
        headerTitle="Excluir Ajuste Prévia"
        message="Tem certeza de que deseja excluir esse ajuste?"
        warningMessage="Esta ação não poderá ser desfeita."
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={() => setOpenDeleteAdjustPreviewModal(false)}
        isLoading={deleteAdjustPreviewLoading}
        onConfirm={() => deleteAdjustPreviewById(deleteAdjustPreviewId!)}
      />
    </>
  )
}
